import { FieldValues } from 'react-hook-form';

import { PropsWithEpiContent } from 'types/Props';

type SelectItem = { caption: string; value: string };

export type FormatFieldDisplayValueOptions = {
    /** Components that use caption/value item lists (e.g. selects, radios) */
    selectiveComponents?: string[];
    /** Components that should be formatted as currency-like numbers */
    currencyComponents?: string[];
    /** Locale for number formatting */
    locale?: string;
    /** Separator for multi-select/array values */
    arraySeparator?: string;
};

const DEFAULT_SELECTIVE: readonly string[] = ['ChoiceElementBlock', 'SelectionElementBlock'] as const;

const DEFAULT_CURRENCY: readonly string[] = ['CurrencyAmountElementBlock', 'CalculatedLoanAmountElementBlock'] as const;

export function formatFieldDisplayValue(
    formItem: PropsWithEpiContent,
    values: FieldValues,
    options: FormatFieldDisplayValueOptions = {},
): string | string[] | undefined {
    const {
        selectiveComponents = [...DEFAULT_SELECTIVE],
        currencyComponents = [...DEFAULT_CURRENCY],
        locale = 'sv-SE',
        arraySeparator = ', ',
    } = options;

    const value = values[formItem.elementName];

    if (value === undefined) return undefined;
    if (value === null) return undefined;
    if (typeof value === 'string' && value === '') return undefined;
    if (Array.isArray(value) && value.length === 0) return undefined;

    const isSelective = !!formItem.component && selectiveComponents.includes(formItem.component as string) && Array.isArray(formItem.items);

    if (isSelective) {
        const items = formItem.items as SelectItem[];

        // Map of value -> caption for quick lookups
        const captionByValue = new Map(items.map((i) => [String(i.value), i.caption]));

        // Single value
        if (!Array.isArray(value)) {
            const hit = captionByValue.get(String(value));
            if (hit !== undefined) return hit;
        }

        // Multi-select: array of values → captions
        if (Array.isArray(value)) {
            const captions = value.map((v) => captionByValue.get(String(v))).filter((c): c is string => typeof c === 'string');
            if (captions.length === 1) return captions[0];
            return captions;
        }
    }

    // Currency-like components: coerce and format if finite; otherwise fall back
    const isCurrency = !!formItem.component && currencyComponents.includes(formItem.component as string);

    if (isCurrency) {
        const num = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(num)) return num.toLocaleString(locale);
        // if not finite, fall through to default stringification
    }

    // Arrays: join nicely (non-selective arrays or when no captions were found)
    if (Array.isArray(value)) {
        return value.map((x) => String(x)).join(arraySeparator);
    }

    // Default: stringify
    return String(value);
}

export function getAdornment(formItem: PropsWithEpiContent): string | undefined {
    return formItem.endAdornment;
}
